import * as React from "react";

import { useSession } from "next-auth/client";
import NextLink from "next/link";
import router from "next/router";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/nextjs";

import { Box, Flex, HStack, Icon, Input, Link, Text, useDisclosure } from "@chakra-ui/react";
import { Button } from "@chakra-ui/button";
import { BsArrowReturnLeft } from "react-icons/bs";
import { FaRocket } from "react-icons/fa";

import { ConfirmPopup } from "components/popups/confirmPopup";
import { CustomSection } from "components/custom-section";
import { SaveIcon } from "components/icons/SaveIcon";

import { resumeSubscription } from "controllers/subscription";
import { firebaseClient, getToken } from "../../firebaseClient";
import * as analytics from "../../utils/analytics";
import { postData, roundUp, showThTokens } from "../../utils/helpers";
import { getAccount, updateUser } from "utils/sessionHelper";
import { useFirebaseUser } from "utils/useFirebaseUser";

export const SubscriptionManagement = (props) => {
  const [session] = useSession();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingCancel, setIsLoadingCancel] = React.useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenCancelFreeTrial,
    onOpen: onOpenCancelFreeTrial,
    onClose: onCloseCancelFreeTrial,
  } = useDisclosure();
  const { isOpen: isOpenRefund, onOpen: onOpenRefund, onClose: onCloseRefund } = useDisclosure();
  const isAuthenticated = useFirebaseUser();
  const [email, setEmail] = React.useState("");

  let isSubscribedFromLessThanMonth =
    session?.user?.data?.subscription?.dateCreated?._seconds < 1000 * 60 * 60 * 24 * 37;
  let isChargedMoreThanMonth = session?.user?.data?.subscriptionLastPayment?.date?._seconds < 1000 * 60 * 60 * 24 * 30;
  const isElligibleRefund =
    isAuthenticated &&
    !session?.user?.data?.subscriptionAlreadyRefunded &&
    session?.user?.data?.subscriptionLastPayment?.status == "succeeded" &&
    isSubscribedFromLessThanMonth &&
    isChargedMoreThanMonth;

  const handleSubscriptionRedirect = async (event) => {
    const db = firebaseClient.firestore();
    const userSessionData = session?.user?.data;
    const userSessionStripeId = userSessionData?.stripeId;
    const userSessionCustomer = userSessionData?.subscription?.customer;

    let customer = userSessionCustomer || userSessionStripeId;

    if (userSessionStripeId && !userSessionCustomer) {
      db.collection("users").doc(session?.user.uid).update({ 'subscription.customer': customer });
    }

    setIsLoading(true);

    if (!customer) {
      let userDoc = await db.collection("users").doc(session?.user.uid).get();
      let userData = userDoc.data();
      customer = userData?.subscription?.customer || userData?.stripeId;
    }

    if (!customer) {
      setIsLoading(false);
      console.log("Error with the session object", session);
      toast.error("An error occurred, contact support at team@tweethunter.io", {
        style: { background: "gray.600", color: "#222" },
      });
      return;
    }

    let data = {
      isLocal: !process.env.NODE_ENV || process.env.NODE_ENV === "development",
      customer: customer,
      userId: session?.user?.uid,
    };

    const res = await fetch("/api/subscriptionManagement", {
      method: "POST",
      headers: new Headers({ "Content-Type": "application/json", token: "" }),
      credentials: "same-origin",
      body: JSON.stringify(data),
    });

    let stripeSession = await res.json();

    if (stripeSession && stripeSession.success) {
      window.location.href = stripeSession.url;
    } else {
      setIsLoading(false);
      console.log("An error happened during checkout. Please contact us for more information");
      toast.error("An error occurred, contact support at team@tweethunter.io", {
        style: { background: "gray.600", color: "#222" },
      });
    }
  };

  const handleRefundRequest = async () => {
    let data: any = {
      idUser: session?.user?.uid,
      lastPayment: session?.user?.data?.subscriptionLastPayment,
    };

    data.lastPayment.date = new Date(data.lastPayment.date._nanoseconds);

    let json = await postData({
      url: "payment-refund",
      token: "",
      data: data,
      session,
    });

    if (json.success) {
      toast.success("Your payment has been refunded, it can take up to 10 days to show up on your side");
      analytics.log("refund_confirmed");
    } else {
      toast.error("An error occurred, contact support at team@tweethunter.io");
      analytics.log("refund_error", { error: json.error });
    }
  };

  const processSubscriptionCancel = async () => {
    analytics.log("cancel_confirmed");
    console.log("cancel_confirmed");

    let dataToSend = {
      tokenUserId: session?.user?.uid,
      token: await getToken(session, "subscriptionCancel"),
      isLocal: !process.env.NODE_ENV || process.env.NODE_ENV === "development",
      customer: session?.user?.data?.stripeId,
      subscription: session?.user?.data?.subscription?.subscription,
      userId: session?.user?.uid,
    };

    const res = await fetch("/api/subscriptionCancel", {
      method: "POST",
      headers: new Headers({ "Content-Type": "application/json", token: "" }),
      credentials: "same-origin",
      body: JSON.stringify(dataToSend),
    });

    let data = await res.json();
    setIsLoadingCancel(false);

    if (data?.success) {
      toast.success("Your subscription has been cancelled");
    } else {
      console.log("An error happened during checkout. Please contact us for more information");
      toast.error("An error occurred, contact support at team@tweethunter.io");
    }
  };

  const initiateUnsubscribeFlow = async () => {
    setIsLoadingCancel(true);
    let response = await fetch("/api/subscriptionInitChurnkey?stripeId=" + session?.user?.data?.stripeId);
    let json = await response.json();

    if (json?.success && json.hash && (window as any)?.churnkey?.init) {
      let hash = json.hash;
      (window as any).churnkey.init("show", {
        customerId: session?.user?.data?.stripeId,
        authHash: hash,
        appId: "qsdeu5psd",
        mode: "live",
        provider: "stripe",
      });

      setIsLoadingCancel(false);
    } else {
      console.log("Failed to launch Churnkey", json, typeof (window as any)?.churnkey?.init);
      Sentry.captureException(new Error("Failed to launch Churnkey"));
      processSubscriptionCancel();
    }
  };

  const renderCancelSubscriptionButton = () => {
    return (
      <Button
        mt={5}
        variant="tertiary"
        justifyContent="flex-start"
        isLoading={isLoadingCancel}
        onClick={async () => {
          analytics.log("cancel_hit");
          if (showThTokens(session)) onOpen();
          else {
            console.log(getAccount(session)?.subStatus);
            if (getAccount(session)?.subStatus?.includes("FreeTrial")) {
              onOpenCancelFreeTrial();
            } else {
              await initiateUnsubscribeFlow();
            }
          }
        }}
      >
        Cancel subscription
      </Button>
    );
  };

  React.useEffect(() => {
    if (getAccount(session)?.emailInvoice) {
      setEmail(getAccount(session).emailInvoice);
    }
  }, [session]);

  return (
    <CustomSection
      title="Email to send invoices"
      description="All invoices will be send to the email address specified bellow"
      css={{ maxW: "600px" }}
    >
      <Flex flexDir="column" pb={10}>
        <Text mt={10}>Email to send invoices</Text>
        <Flex mt={2}>
          <Input
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            placeholder="invoices@mystartup.com"
          />
          <Button
            ml={4}
            isLoading={isLoading}
            onClick={async () => {
              setIsLoading(true);
              await updateUser(session, { emailInvoice: email });
              setIsLoading(false);
            }}
            leftIcon={SaveIcon}
            variant="primary"
            px={4}
          >
            Save
          </Button>
        </Flex>

        <Box>
          <Button
            mt={10}
            leftIcon={
              <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12.4688 3.25C13.1562 3.25 13.7188 3.8125 13.7188 4.5C13.7188 5.21875 13.1562 5.75 12.4688 5.75C11.75 5.75 11.2188 5.21875 11.2188 4.5C11.2188 3.8125 11.75 3.25 12.4688 3.25ZM16.7812 0.625C17 1.625 17 2.4375 16.9375 3.21875C16.9375 6.46875 15.1875 8.40625 12.9375 9.875V13.0625C12.9375 13.5625 12.5625 14.1562 12.0938 14.4062L9 15.9375C8.90625 15.9688 8.78125 16 8.6875 16C8.28125 16 7.96875 15.6875 7.96875 15.25V12.375L7.28125 12.6875C7.1875 12.75 6.625 13 6.15625 12.5L4.46875 10.8438C4.40625 10.7812 3.96875 10.2812 4.28125 9.6875L4.59375 9H1.71875C1.3125 9 0.96875 8.6875 0.96875 8.25C1 8.15625 1 8.03125 1.0625 7.9375L2.59375 4.84375C2.84375 4.375 3.4375 4 3.9375 4H7.125C8.59375 1.75 10.5312 0 13.7812 0C14.5625 0 15.375 0 16.375 0.21875C16.5625 0.28125 16.7188 0.4375 16.7812 0.625ZM12.1875 8.59375C14.5 7.125 15.4688 5.5 15.4688 3.21875C15.4688 2.6875 15.5 2.15625 15.4062 1.59375C14.8438 1.5 14.3438 1.5 13.7812 1.5C11.4688 1.5 9.875 2.5 8.375 4.8125C7.8125 5.96875 6.5 8.59375 5.8125 10.0312L6.96875 11.1875C8.40625 10.5 11.0312 9.1875 12.1875 8.59375Z"
                  fill="currentColor"
                />
              </svg>
            }
            variant={"primary"}
            onClick={() => router.push("/pricing")}
            justifyContent="flex-start"
          >
            {session?.user?.data?.subscription?.isSubscribed ? "Update your plan" : "Subscribe"}
          </Button>
        </Box>
        <Box>
          <Button
            mt={5}
            isLoading={isLoading}
            leftIcon={
              <Icon viewBox="0 0 12 17">
                {" "}
                <svg width="12" height="17" viewBox="0 0 12 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8.25 5C8.65625 5 9 5.34375 9 5.75C9 6.1875 8.65625 6.5 8.25 6.5H3.75C3.3125 6.5 3 6.1875 3 5.75C3 5.34375 3.3125 5 3.75 5H8.25ZM8.25 10.5C8.65625 10.5 9 10.8438 9 11.25C9 11.6875 8.65625 12 8.25 12H3.75C3.3125 12 3 11.6875 3 11.25C3 10.8438 3.3125 10.5 3.75 10.5H8.25ZM3 8.5C3 8.09375 3.3125 7.75 3.75 7.75H8.25C8.65625 7.75 9 8.09375 9 8.5C9 8.9375 8.65625 9.25 8.25 9.25H3.75C3.3125 9.25 3 8.9375 3 8.5ZM2.5 1.78125L3.75 0.6875C4.03125 0.46875 4.4375 0.46875 4.71875 0.6875L6 1.78125L7.25 0.6875C7.53125 0.46875 7.9375 0.46875 8.21875 0.6875L9.5 1.78125L10.75 0.6875C10.9688 0.5 11.2812 0.46875 11.5625 0.59375C11.8125 0.71875 12 0.96875 12 1.25V15.75C12 16.0625 11.8125 16.3125 11.5625 16.4375C11.2812 16.5625 10.9688 16.5312 10.75 16.3438L9.5 15.25L8.21875 16.3438C7.9375 16.5625 7.53125 16.5625 7.25 16.3438L6 15.25L4.71875 16.3438C4.4375 16.5625 4.03125 16.5625 3.75 16.3438L2.5 15.25L1.21875 16.3438C1 16.5312 0.6875 16.5625 0.40625 16.4375C0.15625 16.3125 0 16.0625 0 15.75V1.25C0 0.96875 0.15625 0.71875 0.40625 0.59375C0.6875 0.46875 1 0.5 1.21875 0.6875L2.5 1.78125ZM2 13.6875C2.28125 13.4688 2.6875 13.4688 2.96875 13.6875L4.25 14.7812L5.5 13.6875C5.78125 13.4688 6.1875 13.4688 6.46875 13.6875L7.75 14.7812L9 13.6875C9.28125 13.4688 9.6875 13.4688 9.96875 13.6875L10.5 14.125V2.90625L9.96875 3.34375C9.6875 3.5625 9.28125 3.5625 9 3.34375L7.75 2.25L6.46875 3.34375C6.1875 3.5625 5.78125 3.5625 5.5 3.34375L4.25 2.25L2.96875 3.34375C2.6875 3.5625 2.28125 3.5625 2 3.34375L1.5 2.90625V14.125L2 13.6875Z"
                    fill="#0A66C2"
                  />
                </svg>
              </Icon>
            }
            fontWeight="bold"
            onClick={handleSubscriptionRedirect}
            justifyContent="flex-start"
            variant="secondary"
          >
            Invoices & subscription
          </Button>
        </Box>
        {session?.user?.data?.subscription?.isPaused ? (
          <Flex flexDirection="column">
            <Box mt={10} w="250px">
              <Text>Your subscription is paused</Text>
              <Button
                mt={2}
                variant={"primary"}
                fontWeight="bold"
                onClick={() => {
                  resumeSubscription(session, setIsLoading);
                }}
              >
                Resume subscription
              </Button>
            </Box>
            <Box>{renderCancelSubscriptionButton()}</Box>
          </Flex>
        ) : session?.user?.data?.subscription?.isSubscribed &&
          session.user.data.subStatus?.includes("_CancelRequest") ? (
          <Box mt={10} w="300px">
            <Text>Your subscription will be canceled at the end of the billing period</Text>
            <Button
              mt={2}
              leftIcon={<FaRocket />}
              variant={"primary"}
              onClick={() => {
                resumeSubscription(session, setIsLoading);
              }}
            >
              Reactivate your subscription
            </Button>
            {isElligibleRefund && (
              <Button
                mt={5}
                leftIcon={<BsArrowReturnLeft />}
                onClick={() => {
                  analytics.log("ask_refund_hit");
                  onOpenRefund();
                }}
                variant="secondary"
              >
                Ask for a refund
              </Button>
            )}
          </Box>
        ) : (
          session?.user?.data?.subscription?.isSubscribed && <Box>{renderCancelSubscriptionButton()}</Box>
        )}

        <ConfirmPopup
          isOpen={isOpen}
          onClose={onClose}
          title={`You are about to lose ${roundUp(getAccount(session)?.thTokens) || "your"} TH tokens.`}
          allowClickBackground={true}
          body={
            <Box>
              <Text fontSize="sm" textStyle="body.medium.light">
                Every day you use Tweet Hunter, you accumulate tokens that will get you a piece of the pie if Tweet
                Hunter is ever sold to another company.
                <NextLink href="/tokens" passHref legacyBehavior>
                  <Link ml="1" color="blue.400">
                    learn more
                  </Link>
                </NextLink>
                <br />
                <br />
                <b>You won’t be able to recover those tokens if you come back later.</b>
              </Text>
              <Text fontWeight="bold" mt="6">
                Are you sure you want to move forward?
              </Text>
            </Box>
          }
          // @ts-ignore
          customFooter={
            <HStack justifyContent="space-between" w="100%">
              <Button
                variant="tertiary"
                borderRadius="xl"
                onClick={async () => {
                  onClose();
                  await initiateUnsubscribeFlow();
                }}
              >
                Yes, I want to lose my tokens
              </Button>
              <Button variant={"secondary"} borderRadius="xl" onClick={onClose}>
                Go back
              </Button>
            </HStack>
          }
          callback={() => { }}
        />
        <ConfirmPopup
          isOpen={isOpenCancelFreeTrial}
          onClose={onCloseCancelFreeTrial}
          title={`You will lose access to Tweet Hunter`}
          allowClickBackground={true}
          body={
            <Box>
              <Text fontSize="sm" textStyle="body.medium.light">
                When you cancel your free trial, you immediately lose access to Tweet Hunter.
              </Text>
              <Text fontWeight="bold" mt="6">
                Are you sure you want to continue?
              </Text>
            </Box>
          }
          // @ts-ignore
          customFooter={
            <HStack justifyContent="space-between" w="100%">
              <Button
                variant="tertiary"
                borderRadius="xl"
                onClick={async () => {
                  onCloseCancelFreeTrial();
                  await initiateUnsubscribeFlow();
                }}
              >
                Cancel and delete my account
              </Button>
              <Button variant={"secondary"} borderRadius="xl" onClick={onClose}>
                Back
              </Button>
            </HStack>
          }
          callback={() => { }}
        />
        <ConfirmPopup
          isOpen={isOpenRefund}
          onClose={onCloseRefund}
          title={`Confirm refund request for $` + (session?.user?.data?.subscriptionLastPayment?.amout ?? 1) / 100}
          allowClickBackground={true}
          body={
            "We are happy to provide an easy way for our users to ask for a refund on their first charge!\n\nThe refund will take anywhere between 5 and 10 business days to arrive on your bank account."
          }
          cta="Confirm Refund Request"
          callback={handleRefundRequest}
        />
      </Flex>
    </CustomSection>
  );
};

export default SubscriptionManagement;
